import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import YearFilter from '../../components/dashboard/YearFilter';
import FacultyBarChart from '../../components/dashboard/FacultyBarChart';
import TypeBarChart from '../../components/dashboard/TypeBarChart';
import TimeSeriesChart, {
  type TimeSeriesSeries,
} from '../../components/dashboard/TimeSeriesChart';
import ChartSkeleton from '../../components/dashboard/ChartSkeleton';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../../components/graph/graphConfig';
import { CTU_SPINOFF_COLOR, CTU_STARTUP_COLOR } from '../../lib/colors';
import { getDashboardData } from '../../services/apiService';
import type { DashboardData } from '../../types/api';

const ACCENT_TEAL = '#16A085';
const ACCENT_AMBER = '#E9B949';
const ACCENT_BLUE = '#5B8DEF';

const FALLBACK_YEAR_MIN = 2018;
const FALLBACK_YEAR_MAX = 2026;

interface FacultyAgg {
  faculty: string;
  count: number;
}
interface TypeAgg {
  type: string;
  count: number;
}

interface FilteredData {
  results_by_faculty: FacultyAgg[];
  practical_results_by_faculty: FacultyAgg[];
  active_people_by_faculty: FacultyAgg[];
  projects_by_faculty: FacultyAgg[];
  top_practical_types: TypeAgg[];
  project_types: TypeAgg[];
  results_by_year: any[];
  patents_by_year: any[];
  practical_percentage_over_time: any[];
  projects_count_by_year: any[];
  results_trend: any[];
  results_by_faculty_over_time: any[];
  faculty_keys: string[];
}

function aggregate<T extends { count: number; year?: number }>(
  rows: T[],
  keyField: keyof T,
  yearRange: [number, number] | null,
): { key: string; count: number }[] {
  const source =
    yearRange == null
      ? rows
      : rows.filter(
          (r) =>
            typeof r.year === 'number' &&
            r.year >= yearRange[0] &&
            r.year <= yearRange[1],
        );
  const totals = new Map<string, number>();
  for (const row of source) {
    const key = String(row[keyField]);
    totals.set(key, (totals.get(key) ?? 0) + (row.count ?? 0));
  }
  return Array.from(totals.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

export default function InsightsChartsTab() {
  const { t } = useTranslation(['dashboard', 'common']);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [yearBounds, setYearBounds] = useState<[number, number] | null>(null);
  const [yearRange, setYearRange] = useState<[number, number] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDashboardData()
      .then((data) => {
        if (cancelled) return;
        setDashboardData(data);
        setError(null);
        const years: number[] = [];
        const sources: any[][] = [
          data.results_by_faculty_by_year || [],
          data.practical_results_by_faculty_by_year || [],
          data.active_people_by_faculty_by_year || [],
          data.practical_types_by_year || [],
          data.project_types_by_year || [],
          data.results_by_year || [],
          data.patents_by_year || [],
          data.practical_percentage_over_time || [],
          data.projects_count_by_year || [],
          data.results_trend || [],
        ];
        for (const s of sources) {
          for (const r of s) {
            if (typeof r?.year === 'number' && Number.isFinite(r.year)) years.push(r.year);
          }
        }
        const lo = years.length ? Math.min(...years) : FALLBACK_YEAR_MIN;
        const hi = years.length ? Math.max(...years) : FALLBACK_YEAR_MAX;
        setYearBounds([lo, hi]);
        setYearRange([lo, hi]);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredData = useMemo<FilteredData | null>(() => {
    if (!dashboardData) return null;

    const isFullRange =
      !yearRange ||
      !yearBounds ||
      (yearRange[0] === yearBounds[0] && yearRange[1] === yearBounds[1]);
    const range: [number, number] | null = isFullRange ? null : yearRange;

    const facultyAgg = (rows: any[]): FacultyAgg[] =>
      aggregate(rows, 'faculty', range).map(({ key, count }) => ({
        faculty: key,
        count,
      }));

    const typeAgg = (rows: any[], limit: number): TypeAgg[] =>
      aggregate(rows, 'type', range)
        .slice(0, limit)
        .map(({ key, count }) => ({ type: key, count }));

    return {
      results_by_faculty: facultyAgg(dashboardData.results_by_faculty_by_year || []),
      practical_results_by_faculty: facultyAgg(
        dashboardData.practical_results_by_faculty_by_year || [],
      ),
      active_people_by_faculty: facultyAgg(
        dashboardData.active_people_by_faculty_by_year || [],
      ),
      projects_by_faculty: facultyAgg(
        dashboardData.projects_by_faculty_by_year || [],
      ),
      top_practical_types: typeAgg(dashboardData.practical_types_by_year || [], 12),
      project_types: typeAgg(dashboardData.project_types_by_year || [], 15),
      results_by_year: dashboardData.results_by_year ?? [],
      patents_by_year: dashboardData.patents_by_year ?? [],
      practical_percentage_over_time:
        dashboardData.practical_percentage_over_time ?? [],
      projects_count_by_year: dashboardData.projects_count_by_year ?? [],
      results_trend: (() => {
        const trend = dashboardData.results_trend ?? [];
        const projectsByYear = new Map<number, number>(
          (dashboardData.projects_count_by_year ?? []).map((r: any) => [
            Number(r.year),
            Number(r.count) || 0,
          ]),
        );
        return trend.map((row: any) => {
          const projects = projectsByYear.get(Number(row.year)) ?? 0;
          const total = Number(row.total_results) || 0;
          return {
            ...row,
            projects,
            total_results: Math.max(0, total - projects),
          };
        });
      })(),
      ...(() => {
        const rows = dashboardData.results_by_faculty_by_year ?? [];
        const byYear = new Map<number, Record<string, number>>();
        const facultySet = new Set<string>();
        for (const r of rows as any[]) {
          const year = Number(r?.year);
          const faculty = String(r?.faculty ?? '').trim();
          const count = Number(r?.count) || 0;
          if (!Number.isFinite(year) || !faculty) continue;
          facultySet.add(faculty);
          let bucket = byYear.get(year);
          if (!bucket) {
            bucket = { year };
            byYear.set(year, bucket);
          }
          bucket[faculty] = (bucket[faculty] ?? 0) + count;
        }
        // Sort faculties by total count descending so highest-volume series
        // render on top in the legend.
        const totals = new Map<string, number>();
        for (const f of facultySet) totals.set(f, 0);
        for (const bucket of byYear.values()) {
          for (const f of facultySet) {
            totals.set(f, (totals.get(f) ?? 0) + (bucket[f] ?? 0));
          }
        }
        const faculty_keys = Array.from(facultySet).sort(
          (a, b) => (totals.get(b) ?? 0) - (totals.get(a) ?? 0),
        );
        const data = Array.from(byYear.values()).sort(
          (a, b) => (a.year as number) - (b.year as number),
        );
        // Backfill missing faculties with 0 so lines render continuously.
        for (const row of data) {
          for (const f of faculty_keys) {
            if (typeof row[f] !== 'number') row[f] = 0;
          }
        }
        return { results_by_faculty_over_time: data, faculty_keys };
      })(),
    };
  }, [dashboardData, yearRange, yearBounds]);

  if (loading) {
    return (
      <section className="mb-2" aria-busy="true" aria-label={t('loadingDashboard')}>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-9 w-[320px]" />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton height={420} />
          <ChartSkeleton height={420} />
        </div>
        <Separator className="my-8" />
        <div className="mb-4">
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-3 w-80" />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton height={340} className="lg:col-span-2" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('common:error')}: {error}
        </AlertDescription>
      </Alert>
    );
  }

  if (!dashboardData || !filteredData) return null;

  const isFullRange =
    !yearRange ||
    !yearBounds ||
    (yearRange[0] === yearBounds[0] && yearRange[1] === yearBounds[1]);
  const yearSuffix = isFullRange
    ? undefined
    : yearRange[0] === yearRange[1]
      ? String(yearRange[0])
      : `${yearRange[0]} – ${yearRange[1]}`;

  const trendSeries: TimeSeriesSeries[] = [
    {
      key: 'total_results',
      label: t('charts.resultsTrend.legendTotal'),
      color: ACCENT_BLUE,
      stackId: '1',
      fillOpacity: 0.32,
    },
    {
      key: 'projects',
      label: t('charts.resultsTrend.legendProjects'),
      color: ACCENT_AMBER,
      stackId: '1',
      fillOpacity: 0.5,
    },
    {
      key: 'practical_results',
      label: t('charts.resultsTrend.legendPractical'),
      color: ACCENT_TEAL,
      stackId: '1',
      fillOpacity: 0.55,
    },
  ];

  return (
    <>
      <section className="mb-2">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-ctu-text m-0">
              {t('upperSection.title')}
            </h2>
          </div>
          {yearBounds && yearRange && (
            <YearFilter
              min={yearBounds[0]}
              max={yearBounds[1]}
              value={yearRange}
              onChange={setYearRange}
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <FacultyBarChart
            title={t('charts.resultsByFaculty.title')}
            description={t('charts.resultsByFaculty.description')}
            data={filteredData.results_by_faculty}
            yearSuffix={yearSuffix}
            seriesLabel={t('charts.resultsByFaculty.title')}
          />
          <FacultyBarChart
            title={t('charts.practicalResultsByFaculty.title')}
            description={t('charts.practicalResultsByFaculty.description')}
            data={filteredData.practical_results_by_faculty}
            yearSuffix={yearSuffix}
            seriesLabel={t('charts.practicalResultsByFaculty.title')}
          />
          <FacultyBarChart
            title={t('charts.activeResearchersByFaculty.title')}
            description={t('charts.activeResearchersByFaculty.description')}
            data={filteredData.active_people_by_faculty}
            yearSuffix={yearSuffix}
            seriesLabel={t('charts.activeResearchersByFaculty.title')}
          />
          <FacultyBarChart
            title={t('charts.projectsByFaculty.title')}
            description={t('charts.projectsByFaculty.description')}
            data={filteredData.projects_by_faculty}
            yearSuffix={yearSuffix}
            seriesLabel={t('charts.projectsByFaculty.title')}
          />

          <TypeBarChart
            title={t('charts.topPracticalTypes.title')}
            description={t('charts.topPracticalTypes.description')}
            data={filteredData.top_practical_types}
            yearSuffix={yearSuffix}
            seriesLabel={t('charts.topPracticalTypes.title')}
            color={CTU_SPINOFF_COLOR}
            height={420}
          />

          <TypeBarChart
            title={t('charts.topResultTypes.title')}
            description={t('charts.topResultTypes.description')}
            data={filteredData.project_types}
            yearSuffix={yearSuffix}
            seriesLabel={t('charts.topResultTypes.title')}
            color={ACCENT_AMBER}
            height={420}
          />
        </div>
      </section>

      <Separator className="my-8" />

      <section>
        <div className="mb-4">
          <h2 className="text-xl font-semibold tracking-tight text-ctu-text m-0">
            {t('lowerSection.title')}
          </h2>
          <p className="text-sm text-ctu-text-muted m-0 mt-0.5">
            {t('lowerSection.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TimeSeriesChart
            title={t('charts.resultsOverTime.title')}
            description={t('charts.resultsOverTime.description')}
            data={filteredData.results_by_year}
            series={[
              {
                key: 'count',
                label: t('charts.resultsOverTime.legendTotal'),
                color: ACCENT_BLUE,
                strokeWidth: 2.5,
              },
            ]}
            variant="line"
          />

          <TimeSeriesChart
            title={t('charts.patentsOverTime.title')}
            description={t('charts.patentsOverTime.description')}
            data={filteredData.patents_by_year}
            series={[
              {
                key: 'count',
                label: t('charts.patentsOverTime.title'),
                color: CTU_STARTUP_COLOR,
                fillOpacity: 0.45,
              },
            ]}
            variant="area"
          />

          <TimeSeriesChart
            title={t('charts.practicalPercentageOverTime.title')}
            description={t('charts.practicalPercentageOverTime.description')}
            data={filteredData.practical_percentage_over_time}
            series={[
              {
                key: 'practical_percentage',
                label: t('charts.practicalPercentageOverTime.legendPractical'),
                color: ACCENT_TEAL,
                strokeWidth: 2.5,
                valueFormatter: (v) => `${Number(v).toFixed(2)}%`,
              },
            ]}
            variant="line"
            yDomain={[0, 100]}
          />

          <TimeSeriesChart
            title={t('charts.projectsOverTime.title')}
            description={t('charts.projectsOverTime.description')}
            data={filteredData.projects_count_by_year}
            series={[
              {
                key: 'count',
                label: t('charts.projectsOverTime.legendProjects'),
                color: '#8884d8',
                fillOpacity: 0.45,
              },
            ]}
            variant="area"
          />

          <TimeSeriesChart
            title={t('charts.resultsTrend.title')}
            description={t('charts.resultsTrend.description')}
            data={filteredData.results_trend}
            series={trendSeries}
            variant="area"
            height={340}
            showLegend
          />

          <TimeSeriesChart
            title={t('charts.resultsByFacultyOverTime.title')}
            description={t('charts.resultsByFacultyOverTime.description')}
            data={filteredData.results_by_faculty_over_time}
            series={filteredData.faculty_keys.map((faculty) => ({
              key: faculty,
              label: faculty,
              color: FACULTY_COLORS[faculty] || DEFAULT_NODE_COLOR,
              strokeWidth: 1.75,
            }))}
            variant="line"
            height={340}
            showLegend
          />
        </div>
      </section>
    </>
  );
}
